import apiClient from './api.service';
import cacheService from '../utils/cache.service';
import {
  ActiveAlert,
  SavedSearch,
  SavedSearchAlertConfig,
  SavedSearchDashboardItem,
  SavedSearchFieldGroup,
  SavedSearchGroup,
  SavedSearchPreferencesConfig,
  SavedSearchQueryGroup,
  SavedSearchRunResult
} from '../types/savedSearch.types';
import {
  ProductQuantityTotalsPayload,
  ProductQuantityTotalsResponse
} from '../types/orderProductTotals.types';

type SavedSearchPermissionPayload = { type: 'role' | 'user'; role_id?: number; user_id?: number };

type SavedSearchMutationPayload = {
  name: string;
  icon: string;
  description?: string;
  group_id: number | null;
  global_operator: 'AND' | 'OR';
  query_tree: SavedSearchQueryGroup | null;
  is_dashboard_visible: boolean;
  permissions?: SavedSearchPermissionPayload[];
  alerts?: SavedSearchAlertConfig;
  preferences?: SavedSearchPreferencesConfig;
};

const SEARCH_BY_ID_TTL_MS = 30_000;

class SavedSearchService {
  private searchByIdInflight = new Map<string, Promise<SavedSearch>>();
  private searchByIdCache = new Map<string, { data: SavedSearch; expiresAt: number }>();

  private forgetSearch(storeId: number, searchId: number) {
    this.searchByIdCache.delete(`${storeId}:${searchId}`);
  }
  async getFieldCatalog(storeId: number): Promise<SavedSearchFieldGroup[]> {
    const response = await apiClient.get(`/stores/${storeId}/searches/fields/catalog`);
    return response.data.data;
  }

  async getGroups(storeId: number): Promise<SavedSearchGroup[]> {
    const response = await apiClient.get(`/stores/${storeId}/searches/groups`);
    return response.data.data;
  }

  async createGroup(storeId: number, payload: Partial<SavedSearchGroup>): Promise<SavedSearchGroup> {
    const response = await apiClient.post(`/stores/${storeId}/searches/groups`, payload);
    return response.data.data;
  }

  async updateGroup(storeId: number, groupId: number, payload: Partial<SavedSearchGroup>): Promise<SavedSearchGroup> {
    const response = await apiClient.put(`/stores/${storeId}/searches/groups/${groupId}`, payload);
    return response.data.data;
  }

  async deleteGroup(storeId: number, groupId: number): Promise<void> {
    await apiClient.delete(`/stores/${storeId}/searches/groups/${groupId}`);
  }

  async applyGroupPermissions(
    storeId: number,
    groupId: number,
    permissions: SavedSearchPermissionPayload[]
  ): Promise<{ updatedCount: number }> {
    const response = await apiClient.put(
      `/stores/${storeId}/searches/groups/${groupId}/permissions`,
      { permissions }
    );
    return response.data.data;
  }

  async getSearches(storeId: number): Promise<SavedSearch[]> {
    const response = await apiClient.get(`/stores/${storeId}/searches`);
    return response.data.data;
  }

  async getAccessibleSearches(storeId: number): Promise<Array<{
    id: number;
    group_id: number | null;
    name: string;
    icon: string | null;
    description?: string | null;
    group_name?: string | null;
  }>> {
    const response = await apiClient.get(`/stores/${storeId}/searches/accessible`);
    return response.data.data;
  }

  async getSearchById(storeId: number, searchId: number): Promise<SavedSearch> {
    const key = `${storeId}:${searchId}`;
    const cached = this.searchByIdCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.data;
    const existing = this.searchByIdInflight.get(key);
    if (existing) return existing;
    const request = apiClient.get(`/stores/${storeId}/searches/${searchId}`)
      .then((response) => {
        const data = response.data.data as SavedSearch;
        this.searchByIdCache.set(key, { data, expiresAt: Date.now() + SEARCH_BY_ID_TTL_MS });
        return data;
      })
      .finally(() => {
        this.searchByIdInflight.delete(key);
      });
    this.searchByIdInflight.set(key, request);
    return request;
  }

  async createSearch(storeId: number, payload: SavedSearchMutationPayload): Promise<SavedSearch> {
    const response = await apiClient.post(`/stores/${storeId}/searches`, payload);
    return response.data.data;
  }

  async updateSearch(
    storeId: number,
    searchId: number,
    payload: Partial<SavedSearchMutationPayload>
  ): Promise<SavedSearch> {
    const response = await apiClient.put(`/stores/${storeId}/searches/${searchId}`, payload);
    this.forgetSearch(storeId, searchId);
    return response.data.data;
  }

  async deleteSearch(storeId: number, searchId: number): Promise<void> {
    await apiClient.delete(`/stores/${storeId}/searches/${searchId}`);
    this.forgetSearch(storeId, searchId);
  }

  async previewSearch(
    storeId: number,
    payload: {
      query_tree: SavedSearchQueryGroup | null;
      global_operator: 'AND' | 'OR';
      rows?: number;
      start?: number;
      sort?: string;
    }
  ) {
    const response = await apiClient.post(`/stores/${storeId}/searches/run-preview`, payload, {
      timeout: 25000
    });
    return response.data.data;
  }

  async directSearch(
    storeId: number,
    payload: {
      query_tree: SavedSearchQueryGroup | null;
      global_operator: 'AND' | 'OR';
      rows?: number;
      start?: number;
      sort?: string;
      timeWindow?: string;
      start_at?: string;
      end_at?: string;
      load_all_for_grouping?: boolean;
      load_all_filtered_results?: boolean;
      light_pagination?: boolean;
      saved_search_id?: number;
    }
  ) {
    const timeout =
      payload.load_all_for_grouping || payload.load_all_filtered_results ? 120000 : 25000;
    const response = await apiClient.post(
      `/stores/${storeId}/searches/direct-search`,
      payload,
      { timeout }
    );
    return response.data.data;
  }

  async flowBoardLiveCards(
    storeId: number,
    payload: {
      order_ids?: number[];
      query_tree: SavedSearchQueryGroup | null;
      global_operator?: 'AND' | 'OR';
      phases?: string[];
      rows_per_phase?: number;
      timeWindow?: string;
      start_at?: string;
      end_at?: string;
      resync?: boolean;
    }
  ): Promise<{
    cards: import('../types/order.types').Order[];
    match: { exact: boolean; ids: number[] };
    totals: Record<string, number> | null;
    firstPage: Record<string, {
      orders: import('../types/order.types').Order[];
      total: number;
    }> | null;
  }> {
    const response = await apiClient.post(`/stores/${storeId}/searches/flow-cards`, {
      global_operator: 'AND',
      ...payload
    });
    return response.data.data;
  }

  async flowBoardBootstrap(
    storeId: number,
    payload: {
      query_tree: SavedSearchQueryGroup | null;
      global_operator?: 'AND' | 'OR';
      phases?: string[];
      rows_per_phase?: number;
      sort?: string;
      timeWindow?: string;
      start_at?: string;
      end_at?: string;
    }
  ): Promise<{
    columns: Record<
      string,
      {
        orders: import('../types/order.types').Order[];
        total: number;
        scanTotal?: number;
        offset?: number;
        loaded?: number;
        approximate?: boolean;
        error?: string;
      }
    >;
    meta: { rowsPerPhase: number; phases: string[]; mode?: string; hydrated?: number };
  }> {
    const response = await apiClient.post(
      `/stores/${storeId}/searches/flow-board`,
      {
        global_operator: 'AND',
        ...payload,
      },
      { timeout: 60000 }
    );
    return response.data.data;
  }

  async exportOrders(
    storeId: number,
    payload: Record<string, unknown>
  ): Promise<{ blob: Blob; count: number; total: number; truncated: boolean }> {
    const response = await apiClient.post(`/stores/${storeId}/searches/export`, payload, {
      responseType: 'blob',
      timeout: 120000
    });
    const headers = response.headers || {};
    const read = (name: string) => headers[name] ?? headers[name.toLowerCase()];
    return {
      blob: response.data,
      count: Number(read('x-export-count') || 0),
      total: Number(read('x-export-total') || 0),
      truncated: String(read('x-export-truncated') || '') === '1'
    };
  }

  async productQuantityTotals(
    storeId: number,
    payload: ProductQuantityTotalsPayload
  ): Promise<ProductQuantityTotalsResponse> {
    const response = await apiClient.post(
      `/stores/${storeId}/searches/product-quantity-totals`,
      payload,
      { timeout: 60000 }
    );
    return response.data.data;
  }

  async runSearch(
    storeId: number,
    searchId: number,
    payload: { rows?: number; start?: number; sort?: string; groupId?: number } = {}
  ): Promise<SavedSearchRunResult> {
    const response = await apiClient.post(`/stores/${storeId}/searches/${searchId}/run`, payload);
    return response.data.data;
  }

  async getDashboardSearches(storeId: number): Promise<{
    searches: SavedSearchDashboardItem[];
    accessibleGroupIds: number[];
  }> {
    const response = await apiClient.get(`/stores/${storeId}/searches/dashboard`);
    const body = response.data;
    const searches = Array.isArray(body.data) ? body.data : [];
    const accessibleGroupIds = Array.isArray(body.accessibleGroupIds) ? body.accessibleGroupIds : [];
    return { searches, accessibleGroupIds };
  }

  async getDashboardCounts(storeId: number): Promise<Array<{ id: number; last_result_count: number; last_run_at?: string | null }>> {
    const response = await apiClient.get(`/stores/${storeId}/searches/dashboard/counts`);
    return response.data.data;
  }

  async getDashboardTimeCounts(
    storeId: number,
    options?: {
      groupId?: number;
      timeWindow?: string;
      start_at?: string;
      end_at?: string;
    }
  ): Promise<Record<number, number>> {
    const params = new URLSearchParams();
    if (options?.groupId !== undefined) {
      params.set('groupId', String(options.groupId));
    }
    if (options?.timeWindow) {
      params.set('timeWindow', options.timeWindow);
    }
    if (options?.start_at) {
      params.set('start_at', options.start_at);
    }
    if (options?.end_at) {
      params.set('end_at', options.end_at);
    }
    const query = params.toString();
    const response = await apiClient.get(
      `/stores/${storeId}/searches/dashboard/time-counts${query ? `?${query}` : ''}`
    );
    return response.data.data;
  }

  async getFieldOptions(
    storeId: number,
    fieldId: string,
    options: { search?: string; limit?: number } = {}
  ): Promise<Array<{ label: string; value: string }>> {
    const params = new URLSearchParams();
    if (options.search) params.set('search', options.search);
    if (options.limit) params.set('limit', String(options.limit));
    params.set('_', String(Date.now()));
    const response = await apiClient.get(
      `/stores/${storeId}/searches/fields/${fieldId}/options?${params.toString()}`
    );
    return response.data.data;
  }

  async getDeliveryFieldOptions(
    storeId: number,
    options: { limit?: number } = {}
  ): Promise<{
    delivery_statuses: Array<{ label: string; value: string }>;
    delivery_companies: Array<{ label: string; value: string }>;
    delivery_situations: Array<{ label: string; value: string }>;
  }> {
    const params = new URLSearchParams();
    if (options.limit != null) params.set('limit', String(options.limit));
    params.set('_', String(Date.now()));
    const response = await apiClient.get(
      `/stores/${storeId}/searches/fields/delivery-options?${params.toString()}`
    );
    return response.data.data;
  }

  async getActiveAlerts(storeId: number, force = false): Promise<ActiveAlert[]> {
    const cacheKey = 'saved_search_alerts';
    if (!force) {
      const cached = cacheService.get<ActiveAlert[]>(storeId, cacheKey);
      if (cached) return cached;
    }

    const response = await apiClient.get(`/stores/${storeId}/searches/alerts`);
    const data = response.data.data;
    // Short TTL: board/Orders always pass force after sockets; this covers other callers.
    cacheService.set(storeId, cacheKey, data, 60 * 1000);
    return data;
  }
}

export default new SavedSearchService();

